import { CommonModule } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
  inject,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import {
  OAuthModule,
  OAuthModuleConfig,
  OAuthStorage,
} from "angular-oauth2-oidc";
import { SharedModule } from "app/shared/shared.module";
import { environment } from "environments/environment";
import { AuthGuardWithForcedLogin } from "./auth-guard-with-forced-login.service";
import { AuthGuard } from "./auth-guard.service";
import { AuthService } from "./auth.service";
import { AuthorizationService } from "./authorization.service";
import { ConfirmDialogService } from "./confirm-dialog.service";
import { ConfirmDialogComponent } from "./confirm-dialog/confirm-dialog.component";
import { CoreService } from "./core.service";
import { ErrorMessageComponent } from "./error-message/error-message.component";
import { ForbiddenComponent } from "./forbidden/forbidden.component";
import { throwIfAlreadyLoaded } from "./module-import-guard";
import { onAuthorizationBiModeInit } from "./auth-bi-mode/auth-bi-mode-initializer";
import { AuthBiModeService } from "./auth-bi-mode/auth-bi-mode.service";
import { PrefixedOAuthStorageService } from "./prefixed-oauth-storage.service";
import packageDotJson from "../../../package.json";
import {
  OAuthLogger,
  OAuthResourceServerErrorHandler,
  OAuthService,
} from "angular-oauth2-oidc";
import { AutoRefreshOAuthInterceptor } from "./autorefresh-oauth.interceptor";

function createOAuthInterceptor(config: OAuthModuleConfig) {
  return () => {
    const oAuthService = inject(OAuthService);
    const errorHandler = inject(OAuthResourceServerErrorHandler);
    const logger = inject(OAuthLogger);
    return new AutoRefreshOAuthInterceptor(
      oAuthService,
      errorHandler,
      logger,
      config,
    );
  };
}

export const authModuleConfig: OAuthModuleConfig = {
  resourceServer: {
    allowedUrls: [environment.apiHost, environment.citizenApiHost],
    sendAccessToken: true,
  },
};

// We need a factory since localStorage is not available at AOT build time
export function storageFactory(): OAuthStorage {
  return new PrefixedOAuthStorageService(
    localStorage,
    packageDotJson.name,
    environment.oauthParameter.showDebugInformation,
  );
}

@NgModule({
  imports: [
    CommonModule,
    TranslateModule.forChild(),
    HttpClientModule,
    SharedModule,
    OAuthModule.forRoot({
      resourceServer: {
        sendAccessToken: false, // disable the one coming with the OAuth library because we're providing our own injector.
      },
    }),
  ],
  exports: [ErrorMessageComponent, ConfirmDialogComponent, ForbiddenComponent],
  declarations: [
    ErrorMessageComponent,
    ConfirmDialogComponent,
    ForbiddenComponent,
  ],
  providers: [
    AuthService,
    AuthGuard,
    AuthGuardWithForcedLogin,
    AuthorizationService,
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: createOAuthInterceptor(authModuleConfig),
      multi: true,
    },
  ],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwIfAlreadyLoaded(parentModule, "CoreModule");
  }

  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        {
          provide: APP_INITIALIZER,
          multi: true,
          useFactory: onAuthorizationBiModeInit,
          deps: [AuthService, AuthBiModeService, HttpClient],
        },
        { provide: OAuthModuleConfig, useValue: authModuleConfig },
        { provide: OAuthStorage, useFactory: storageFactory },
        CoreService,
        ConfirmDialogService,
      ],
    };
  }
}
